import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { activityIcon, useActivities, ACTIVITY_SCHEMA } from '../components/Activities';
import { EditButton } from '../components/admin/EditorShell';
import { SchemaEditor } from '../components/admin/SchemaEditor';
import { mockData } from '../mock';
import { Seo, breadcrumb } from '../components/Seo';
import { useT } from '../context/LanguageContext';

const Aktiviteter = () => {
  const { t } = useT();
  const activities = useActivities();
  const [editing, setEditing] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.replace('#', '');
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="bg-black min-h-screen text-white" data-testid="activities-page">
      <Seo title={t('Aktivitetar | Refuel Weekend 2027')} description={t('Fotball, paintball, volleyball, KRIK-økt, bading, buldring, gaming og spel – sjå alle aktivitetane på Refuel Weekend 2027 i Øygarden.')} path="/aktiviteter" jsonLd={breadcrumb([{ name: 'Refuel Weekend', path: '/' }, { name: t('Aktivitetar'), path: '/aktiviteter' }])} />
      <Navbar />
      <header className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${mockData.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/80 to-black" />
        <div className="relative max-w-[1200px] mx-auto px-4 md:px-8 pt-12 pb-14">
          <Link to="/#aktiviteter" data-testid="activities-back-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm mb-8">
            <ArrowLeft size={16} /> {t('Tilbake')}
          </Link>
          <div className="flex items-center justify-between gap-4 mb-2"><p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm">Refuel Weekend 27</p><EditButton onClick={() => setEditing(true)} testId="edit-activities-page-button" /></div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Aktivitetsoversikt')}</h1>
          <div className="flex flex-wrap gap-2 mt-8">
            {activities.map((a) => (
              <a key={a.slug} href={`#${a.slug}`} data-testid={`activity-chip-${a.slug}`} className="px-4 py-2 rounded-full border border-[#ff2e2e]/60 text-[#ff2e2e] text-sm font-bold hover:bg-[#ff2e2e] hover:text-black transition-colors">
                {t(a.title)}
              </a>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 pb-24 space-y-10 md:space-y-14">
        {activities.map((a, i) => (
          <article key={a.slug} id={a.slug} data-testid={`activity-section-${a.slug}`} className="scroll-mt-28 grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-10 items-center rounded-[2rem] bg-[#1a1424] border border-white/5 overflow-hidden">
            <div className={`relative aspect-[16/9] md:aspect-auto md:h-full min-h-[240px] ${i % 2 ? 'md:order-2' : ''}`}>
              {a.image ? (
                <img src={a.image} alt={`${a.title} på Refuel Weekend`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#2a1b3d] to-[#120c1b] flex items-center justify-center text-[#ff2e2e]">{activityIcon(a.slug, 96)}</div>
              )}
            </div>
            <div className="p-6 md:p-10 min-w-0">
              <div className="w-12 h-12 rounded-full bg-[#ff2e2e]/10 text-[#ff2e2e] flex items-center justify-center mb-5">{activityIcon(a.slug, 24)}</div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(a.title)}</h2>
              <p className="text-white/75 text-base md:text-lg leading-relaxed break-words">{t(a.description)}</p>
            </div>
          </article>
        ))}
        <div className="text-center pt-6">
          <p className="text-7xl md:text-8xl font-bold text-[#ff2e2e]/20 tracking-tighter" style={{ fontFamily: "'Oktah Round', sans-serif" }}>RW27</p>
        </div>
      </main>
      <Footer />
      {editing && <SchemaEditor contentKey="activities" title="Rediger aktivitetar" initial={activities.map((a) => ({ id: a.slug, ...a }))} schema={ACTIVITY_SCHEMA} itemLabel="aktivitet" onClose={() => setEditing(false)} />}
    </div>
  );
};

export default Aktiviteter;
